import {
    DropdownLabel,
    DropdownWrapper,
    StyledArrowDown,
    StyledVariantDropdown,
    StyledVariantDropdownItem,
} from './styled';
import { useRouter } from 'next/router';
import useRedirectToBestMatch from '~/features/product-details-page/hooks/useRedirectToBestMatch';
import { useEffect, useMemo, useState } from 'react';
import { VariantPickerProps, VariationAttributesValues } from '../PDPVariants';

type VariantDropdownProps = VariantPickerProps;

const VariantDropdown = ({ variant, onClick, id, selectedAttributes }: VariantDropdownProps) => {
    const router = useRouter();
    const { redirectToBestMatch } = useRedirectToBestMatch();
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(variant.selectedValue?.value);

    const handleChange = (value: VariationAttributesValues) => {
        setIsOpen(false);

        if (onClick) {
            onClick({ ...value, name: id });

            setSelected(value.value);

            const selectedOption = variant.values.find((option) => option.value === value.value);

            if (selectedOption?.href) {
                router.replace(selectedOption.href, undefined, { shallow: true });
            } else {
                redirectToBestMatch({ ...selectedAttributes, [id]: value.value }, id);
            }
        }
    };

    const getNumericValue = (variant: VariationAttributesValues): number => {
        const numericPart = variant.value.replace(',', '.').split(' ')[0];
        return parseFloat(numericPart);
    };

    const { isSelectedOrderable, sortedVariantValues } = useMemo(() => {
        const sortedVariantValues = [...variant.values]
            .sort((a, b) => {
                const aValueNumber = getNumericValue(a);
                const bValueNumber = getNumericValue(b);

                if (!isNaN(aValueNumber) && !isNaN(bValueNumber)) {
                    return aValueNumber - bValueNumber;
                }

                return a.value.localeCompare(b.value);
            })
            .map((value) => ({
                ...value,
                className: !value.orderable ? 'disabled' : '',
            }))
            .filter((value) => !!value.href);

        const isSelectedOrderable =
            variant.values.find((item) => item.value === variant.selectedValue?.value)?.orderable ??
            false;

        return {
            isSelectedOrderable,
            sortedVariantValues,
        };
    }, [variant.values, variant.selectedValue?.value]);

    const dropDownItems = useMemo(() => {
        if (!isOpen) return null;

        return (
            <div>
                {sortedVariantValues.map((item) => (
                    <StyledVariantDropdownItem
                        key={item.value}
                        onClick={() => handleChange(item)}
                        className={`${item.className} ${variant.selectedValue?.value === item.value ? 'is-selected' : ''}`}
                    >
                        {item.label}
                    </StyledVariantDropdownItem>
                ))}
            </div>
        );
    }, [isOpen, sortedVariantValues, variant.selectedValue?.value]);

    useEffect(() => {
        setSelected(variant.selectedValue?.value);
    }, [variant.selectedValue?.value]);

    if (variant.values.length <= 1) {
        return null;
    }

    return (
        <DropdownWrapper isSelectedOrderable={isSelectedOrderable} isOpen={isOpen}>
            <DropdownLabel>{variant.name}</DropdownLabel>
            <StyledVariantDropdown onClick={() => setIsOpen(!isOpen)}>
                <span className="is-selected">{selected}</span>
                <StyledArrowDown />
            </StyledVariantDropdown>

            {dropDownItems}
        </DropdownWrapper>
    );
};

export default VariantDropdown;
